/* ============================================================================
   Phone input sanitiser — applies to every input[type="tel"] in the theme.

   The pattern attribute only reports a problem once the field is validated. This
   stops the bad input arriving in the first place: slashes, backslashes, letters
   and stray symbols are dropped as they are typed, and the digit count is capped
   at 15 (E.164's maximum), so a number cannot be padded past a plausible length.

   Deliberately a single delegated listener on document rather than a custom
   element: there are eleven tel inputs across ten sections, some inside the cart
   drawer and RFQ modal that are injected after first paint, and delegation covers
   those without each section having to opt in.

   What it does NOT do is reformat as you type — no auto-grouping into
   (555) 123-4567. That grouping is country-specific and this store takes
   international B2B numbers (+44, +61, +92 are all in use), so imposing one
   country's shape would mangle the rest. Rewriting the value on every keystroke
   also moves the caret, which breaks mid-number editing and pasting. Caret
   position is preserved here precisely because characters can be removed.
   ========================================================================== */
(() => {
  const ALLOWED = /[0-9+\s().\-]/;
  const MAX_DIGITS = 15;

  /**
   * Strips disallowed characters and caps the digit count, tracking where the
   * caret should end up so removing a character mid-string does not jump the
   * cursor to the end.
   * @param {string} value - Raw field value.
   * @param {number} caret - Current selectionStart.
   * @returns {{value: string, caret: number}}
   */
  function sanitise(value, caret) {
    let out = '';
    let digits = 0;
    let newCaret = caret;

    for (let i = 0; i < value.length; i += 1) {
      const ch = value[i];
      let keep = ALLOWED.test(ch);

      if (keep && ch >= '0' && ch <= '9') {
        if (digits >= MAX_DIGITS) keep = false;
        else digits += 1;
      }

      // A plus sign only means anything as a country prefix.
      if (keep && ch === '+' && out.length > 0) keep = false;

      if (keep) out += ch;
      else if (i < caret) newCaret -= 1;
    }

    return { value: out, caret: Math.max(0, newCaret) };
  }

  document.addEventListener('input', (evt) => {
    const el = evt.target;
    if (!el || el.tagName !== 'INPUT' || el.type !== 'tel') return;

    const caret = el.selectionStart === null ? el.value.length : el.selectionStart;
    const result = sanitise(el.value, caret);
    if (result.value === el.value) return;

    el.value = result.value;
    try {
      el.setSelectionRange(result.caret, result.caret);
    } catch (e) {
      // Some browsers refuse setSelectionRange on certain input types; the
      // sanitised value still stands, only the caret position is lost.
    }
  });
})();
